import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams, useBlocker } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { apiClient } from '../../services/apiClient';
import { brandConfig } from '../../config/brands';
import { useUser } from '../../features/auth/useUser';
import { useToast } from '../../hooks/useToast';
import LogoManagementModal from '../../components/LogoManagementModal';
import LogoEditModal from '../../components/LogoEditModal';
import { BrandCutSection } from './BrandCutSection';
import { SoundLogoSection } from './SoundLogoSection';
import { BrandFontField } from './BrandFontField';
import { BrandUrlSection } from './BrandUrlSection';
import { InlineEditText } from './InlineEditText';
import { isComposingEnter } from '../../features/videoEditor/components/imeKey';
import { BrandToneMannerSection } from './BrandToneMannerSection';
import { regenerateBrandLogoSheet } from '../../features/brands/regenerateBrandLogoSheet';
import { Label } from '../../components/common/Label';
import { HelpTooltip } from '../../components/common/HelpTooltip';
import { BackButton } from '../../components/common/BackButton';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { useConfirmDialog } from '../../components/common/useConfirmDialog';
import { ToggleSwitch } from '../../components/common/ToggleSwitch';
import { GenerateButton } from '../../components/generation/common/GenerateButton';
import { useCreditBadgeText } from '../../components/generation/hooks/useGenerationCredits';
import { useCreditRates } from '../../features/organizations/hooks';
import { siteProposalCreditsPerRun } from '../../features/brands/siteProposal';
import { useBrandUrls } from '../../features/brands/urls';
import { DESIGN_PALETTE_ROLES, normalizeDesignLanguage } from '../../features/brands/designLanguage';
import { logoCardBackground, pickPrimaryLogo } from '../../features/brands/logoAttributes';
import { Check, Plus, X } from 'lucide-react';
import type { LabelVariant } from '../../components/common/Label';
import { formatDateTime as formatDate } from '../../utils/formatters';
import '../../styles/inline-edit.css';
import '../../components/common/CommonTable.css';
import '../products/ProductsPageHeader.css';
import './BrandDetailPage.css';
import '../../styles/buttons.css';

// Brand / BrandLogo / ECBrandMapping の正本は features/brands/types.ts
import type { Brand, BrandLogo, ECBrandMapping } from '../../features/brands/types';

interface BrandStats {
  total_products: number;
  products_by_platform: {
    platform: string;
    marketplace_id?: string;
    count: number;
  }[];
  active_mappings: number;
  total_logos: number;
  cloud_materials_count: number;
  temporary_materials_count: number;
}

interface GuidelineImage {
  id: string;
  brand_id: string;
  storage_path: string;
  file_name: string;
  display_order: number;
  description?: string;
  url?: string;
}

// ロゴ属性ラベル
const BACKGROUND_LABELS: Record<string, string> = {
  white_back: '白バック',
  black_back: '黒バック',
  transparent: '透過',
};

const COLOR_TYPE_LABELS: Record<string, string> = {
  full_color: 'フルカラー',
  monochrome: 'モノクロ（黒）',
  white: '白抜き',
  solid_color: '単色（ブランドカラー）',
  grayscale: 'グレースケール',
  gradient: 'グラデーション',
  flat: 'フラット',
};

const LAYOUT_LABELS: Record<string, string> = {
  horizontal: '横組み',
  vertical: '縦組み',
  square: 'スクエア',
  logotype: 'ロゴタイプのみ',
  symbol: 'シンボル',
  lockup: 'アイコン+ロゴ',
  app_icon: 'アプリアイコン',
  circle: 'サークル',
};

// ロゴの属性を表示用テキストに変換
const getLogoAttributeText = (logo: BrandLogo): string[] => {
  const parts: string[] = [];
  if (logo.background && BACKGROUND_LABELS[logo.background]) {
    parts.push(BACKGROUND_LABELS[logo.background]);
  }
  if (logo.color_type && COLOR_TYPE_LABELS[logo.color_type]) {
    parts.push(COLOR_TYPE_LABELS[logo.color_type]);
  }
  if (logo.layout && LAYOUT_LABELS[logo.layout]) {
    parts.push(LAYOUT_LABELS[logo.layout]);
  }
  return parts;
};

/** ブランド詳細のタブ (案B・利用者指示 2026-10-30)。?tab= で開く位置を持つ (既定 = 概要) */
const BRAND_TABS = [
  { key: 'overview', label: '概要' },
  { key: 'tone', label: 'トーン＆マナー' },
  { key: 'assets', label: 'アセット' },
  { key: 'rules', label: 'ガイドライン' },
  { key: 'links', label: '連携と関連' },
] as const;
type BrandTabKey = typeof BRAND_TABS[number]['key'];

const roleLabel = (role: string) => DESIGN_PALETTE_ROLES.find((r) => r.value === role)?.label ?? role;

// タグ管理ページのテーブルと同じピル型の数字表示
function CountPill({
  count,
  href,
  variant,
}: {
  count: number;
  href?: string | null;
  variant?: 'personal';
}) {
  const variantClass = variant === 'personal' ? ' count-pill--personal' : '';
  if (count === 0) {
    return <span className={`count-pill${variantClass} count-pill--zero`}>0</span>;
  }
  const className = `count-pill${variantClass}`;
  if (href) {
    return (
      <Link to={href} className={className}>
        {count.toLocaleString()}
      </Link>
    );
  }
  return <span className={className}>{count.toLocaleString()}</span>;
}

export default function BrandDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  useUser();
  const toast = useToast();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const isCreating = !id;

  // 新規作成フォーム
  const [createForm, setCreateForm] = useState({
    brand_name: '',
    brand_code: '',
    description: '',
    tagline: '',
    brand_main_color: '',
    brand_sub_color: '',
    brand_accent_color: '',
    brand_font_families: [] as string[],
  });
  const [createErrors, setCreateErrors] = useState<Set<string>>(new Set());

  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [editingLogo, setEditingLogo] = useState<BrandLogo | null>(null);

  // ロゴドラッグ&ドロップ用
  const [draggedLogoId, setDraggedLogoId] = useState<string | null>(null);
  const [dragOverLogoId, setDragOverLogoId] = useState<string | null>(null);
  const dragCounter = useRef(0);

  // ガイドライン画像用
  const guidelineFileInputRef = useRef<HTMLInputElement>(null);
  const [isGuidelineUploading, setIsGuidelineUploading] = useState(false);
  const [guidelineDragOver, setGuidelineDragOver] = useState(false);

  // Fetch brand details
  const { data: brand, isLoading, error } = useQuery({
    queryKey: ['brand', id],
    queryFn: async () => {
      const response = await apiClient.get(`/brands/${id}`);
      return response.data.brand;
    },
    enabled: !!id && !isCreating,
  });

  // 新規作成時・取得前は undefined → routes.tsx の handle が使われる
  useDocumentTitle(brand?.brand_name || undefined);

  // タブ: ?tab= で開く位置を持つ (共有・再読み込みで同じタブに戻る)。パネルは全部マウントしたまま隠す
  const tabParam = searchParams.get('tab');
  const activeTab: BrandTabKey = BRAND_TABS.some((t) => t.key === tabParam) ? (tabParam as BrandTabKey) : 'overview';
  const selectTab = (key: BrandTabKey) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'overview') next.delete('tab');
      else next.set('tab', key);
      return next;
    }, { replace: true });
  };

  // 見出し帯の「AIでブランド情報を再生成」: トーン＆マナーのタブへ切り替え、URL のモーダルを開かせる (結果もそのタブに出る)
  const [regenerateRequest, setRegenerateRequest] = useState(0);
  const [regenerating, setRegenerating] = useState(false);
  const creditRates = useCreditRates();
  const regenerateCreditBadge = useCreditBadgeText(siteProposalCreditsPerRun(creditRates.data?.rates));

  // 概要ボード用: ブランドURL・ブランドカット・サウンドロゴ (各タブのセクションと同じ queryKey なので取得は共有される)
  const { data: brandUrlsData } = useBrandUrls(isCreating ? undefined : id);
  const brandUrls = brandUrlsData ?? [];
  const { data: brandCutsData } = useQuery({
    queryKey: ['brand-cuts', id],
    queryFn: async () => (await apiClient.get<{ brand_cuts: unknown[] }>(`/brands/${id}/brand-cuts`)).data.brand_cuts,
    enabled: !!id && !isCreating,
  });
  const { data: soundLogosData } = useQuery({
    queryKey: ['sound-logos', id],
    queryFn: async () => (await apiClient.get<{ sound_logos: unknown[] }>(`/brands/${id}/sound-logos`)).data.sound_logos,
    enabled: !!id && !isCreating,
  });
  const brandCutCount = brandCutsData?.length ?? 0;
  const soundLogoCount = soundLogosData?.length ?? 0;

  // Fetch brand stats
  const { data: stats } = useQuery<BrandStats>({
    queryKey: ['brand-stats', id],
    queryFn: async () => {
      const response = await apiClient.get(`/brands/${id}/stats`);
      return response.data;
    },
    enabled: !!id && !isCreating,
  });

  // Fetch guideline images
  const { data: guidelinesData } = useQuery<{ guidelines: GuidelineImage[] }>({
    queryKey: ['brand-guidelines', id],
    queryFn: async () => {
      const response = await apiClient.get(`/brands/${id}/guidelines`);
      return response.data;
    },
    enabled: !!id && !isCreating,
  });

  // Create brand mutation
  const createBrandMutation = useMutation({
    mutationFn: async (data: { brand_name: string; brand_code?: string; description?: string; tagline?: string; brand_main_color?: string; brand_sub_color?: string; brand_accent_color?: string; brand_font_families?: string[] }) => {
      const response = await apiClient.post('/brands', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      toast.success('ブランドを作成しました');
      navigate(`/brands/${data.brand.id}`, { replace: true });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'ブランドの作成に失敗しました');
    },
  });
  const guidelines = guidelinesData?.guidelines || [];

  // Update brand mutation
  const updateBrandMutation = useMutation({
    mutationFn: async (data: Partial<Brand>) => {
      const response = await apiClient.patch(`/brands/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand', id] });
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      setEditingField(null);
    },
  });

  // Delete brand mutation
  const deleteBrandMutation = useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/brands/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      navigate('/brands');
    },
  });

  // ロゴ並べ替えmutation
  const reorderLogosMutation = useMutation({
    mutationFn: async (order: { id: string; display_order: number }[]) => {
      await apiClient.post(`/brands/${id}/logos/reorder`, { order });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand', id] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'ロゴの並べ替えに失敗しました');
    },
  });

  // ロゴ1枚シートの自動再生成 (ロゴCRUD後 / 未生成ブランドのバックフィル)
  // 全 CRUD は ['brand', id] を invalidate するため、ここ1か所で brand の
  // source_token 乖離を検知して再生成すれば追加/編集/削除/並べ替え/初回を統一的にカバーできる。
  const [logoSheetError, setLogoSheetError] = useState(false);
  const logoSheetRegeneratingRef = useRef(false);
  const logoSheetAttemptedTokenRef = useRef<string | null>(null);

  const runLogoSheetRegen = useCallback(
    async (force = false) => {
      if (!id || !brand) return;
      const currentToken = brand.logo_source_token ?? '';
      const storedToken = brand.logo_sheet_source_token ?? null;
      const logos = brand.logos ?? [];

      // ロゴも既存シートも無ければ何もしない (不要な DELETE を避ける)
      if (logos.length === 0 && !brand.logo_sheet_path) return;

      if (!force) {
        if (storedToken === currentToken) return; // 既に最新
        if (logoSheetAttemptedTokenRef.current === currentToken) return; // この世代は試行済み
        if (logoSheetRegeneratingRef.current) return;
      }

      logoSheetAttemptedTokenRef.current = currentToken;
      logoSheetRegeneratingRef.current = true;
      setLogoSheetError(false);
      try {
        await regenerateBrandLogoSheet(id, logos, currentToken, { brandName: brand.brand_name });
        // 反映後 stored===current に同期するため再取得 (effect の再発火は guard で止まる)
        queryClient.invalidateQueries({ queryKey: ['brand', id] });
      } catch {
        setLogoSheetError(true);
      } finally {
        logoSheetRegeneratingRef.current = false;
      }
    },
    [id, brand, queryClient]
  );

  useEffect(() => {
    runLogoSheetRegen(false);
  }, [runLogoSheetRegen]);

  // ガイドライン画像アップロードmutation
  const uploadGuidelineMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/brands/${id}/guidelines`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-guidelines', id] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'アップロードに失敗しました');
    },
  });

  // ガイドライン画像削除mutation
  const deleteGuidelineMutation = useMutation({
    mutationFn: async (guidelineId: string) => {
      await apiClient.delete(`/brands/${id}/guidelines/${guidelineId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-guidelines', id] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || '削除に失敗しました');
    },
  });

  // ロゴドラッグ&ドロップハンドラー
  const handleLogoDragStart = useCallback((e: React.DragEvent, logoId: string) => {
    setDraggedLogoId(logoId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', logoId);
  }, []);

  const handleLogoDragEnd = useCallback(() => {
    setDraggedLogoId(null);
    setDragOverLogoId(null);
    dragCounter.current = 0;
  }, []);

  const handleLogoDragEnter = useCallback(
    (e: React.DragEvent, logoId: string) => {
      e.preventDefault();
      dragCounter.current++;
      if (logoId !== draggedLogoId) {
        setDragOverLogoId(logoId);
      }
    },
    [draggedLogoId]
  );

  const handleLogoDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverLogoId(null);
    }
  }, []);

  const handleLogoDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleLogoDrop = useCallback(
    async (e: React.DragEvent, targetLogoId: string) => {
      e.preventDefault();
      setDragOverLogoId(null);
      dragCounter.current = 0;

      if (!draggedLogoId || draggedLogoId === targetLogoId || !brand?.logos) return;

      const logos = [...brand.logos];
      const draggedIndex = logos.findIndex((l) => l.id === draggedLogoId);
      const targetIndex = logos.findIndex((l) => l.id === targetLogoId);

      if (draggedIndex === -1 || targetIndex === -1) return;

      const [draggedLogo] = logos.splice(draggedIndex, 1);
      logos.splice(targetIndex, 0, draggedLogo);

      const newOrder = logos.map((logo, index) => ({
        id: logo.id,
        display_order: index,
      }));

      try {
        await reorderLogosMutation.mutateAsync(newOrder);
      } catch (error) {
        console.error('Failed to reorder logos:', error);
      }

      setDraggedLogoId(null);
    },
    [draggedLogoId, brand?.logos, reorderLogosMutation]
  );

  // ガイドライン画像ハンドラー
  const handleGuidelineFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const remainingSlots = 1 - guidelines.length;
      if (remainingSlots <= 0) {
        toast.error('ガイドライン画像は1枚までです');
        return;
      }

      const filesToUpload = Array.from(files).slice(0, remainingSlots);

      setIsGuidelineUploading(true);
      try {
        for (const file of filesToUpload) {
          await uploadGuidelineMutation.mutateAsync(file);
        }
      } finally {
        setIsGuidelineUploading(false);
        if (guidelineFileInputRef.current) {
          guidelineFileInputRef.current.value = '';
        }
      }
    },
    [uploadGuidelineMutation, guidelines.length, toast]
  );

  const handleGuidelineDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setGuidelineDragOver(false);

      const remainingSlots = 1 - guidelines.length;
      if (remainingSlots <= 0) {
        toast.error('ガイドライン画像は1枚までです');
        return;
      }

      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;

      const validFiles = Array.from(files)
        .filter((file) => ['image/png', 'image/jpeg', 'image/webp'].includes(file.type))
        .slice(0, remainingSlots);

      if (validFiles.length === 0) {
        toast.error('PNG, JPG, WebP形式のファイルのみアップロードできます');
        return;
      }

      setIsGuidelineUploading(true);
      try {
        for (const file of validFiles) {
          await uploadGuidelineMutation.mutateAsync(file);
        }
      } finally {
        setIsGuidelineUploading(false);
      }
    },
    [uploadGuidelineMutation, guidelines.length, toast]
  );

  const handleGuidelineDelete = useCallback(
    async (guidelineId: string) => {
      if (await confirm({ title: 'このガイドライン画像を削除しますか？', confirmLabel: '削除する' })) {
        deleteGuidelineMutation.mutate(guidelineId);
      }
    },
    [deleteGuidelineMutation, confirm]
  );

  // 旧来の自社ブランド設定はリンクされていないが、URL に setOwnBrand=true が
  // 残っている場合のため、パラメータをクリアするだけにする
  useEffect(() => {
    if (searchParams.get('setOwnBrand') === 'true') {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // トーン＆マナー (配色・書体など) の未保存の変更。子から onDirtyChange で受ける
  const [toneMannerDirty, setToneMannerDirty] = useState(false);
  const isDirty = toneMannerDirty || !!(
    editingField &&
    brand &&
    (((brand as any)[editingField] ?? '') as string) !== editValue
  );

  // SPAナビゲーションの警告（サイドメニュー遷移・戻るボタン等）。共通の ConfirmDialog を出す (動画エディタ・案件詳細と同じ方式)
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    isDirty && currentLocation.pathname !== nextLocation.pathname
  );

  // タブ閉じ・リロードの警告
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const startEditing = async (field: string, currentValue: string) => {
    if (editingField && editingField !== field && brand) {
      const original = ((brand as any)[editingField] ?? '') as string;
      if (editValue !== original) {
        if (!(await confirm({ title: '保存していない変更があります', message: '破棄してよろしいですか？', confirmLabel: '破棄する', variant: 'warning' }))) {
          return;
        }
      }
    }
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  const saveField = () => {
    if (!editingField) return;

    updateBrandMutation.mutate(
      { [editingField]: editValue },
      {
        onSuccess: () => {
          setEditingField(null);
          setEditValue('');
        },
      }
    );
  };

  const handleDelete = async () => {
    if (await confirm({ title: `「${brand?.brand_name}」を削除してもよろしいですか？`, message: '注意: 商品が紐づいている場合は削除できません。', confirmLabel: '削除する' })) {
      deleteBrandMutation.mutate();
    }
  };

  const getPlatformLabel = (platform: string) => {
    const labels: Record<string, string> = {
      amazon: 'Amazon',
      rakuten: '楽天市場',
      yahoo: 'Yahoo!ショッピング',
      manual: 'マニュアル登録',
    };
    return labels[platform] || platform;
  };

  const getConfidenceLabel = (confidence: string) => {
    const labels: Record<string, string> = {
      auto: '自動',
      manual: '手動',
      verified: '確認済み',
    };
    return labels[confidence] || confidence;
  };

  const getConfidenceVariant = (confidence: string): LabelVariant => {
    const variants: Record<string, LabelVariant> = {
      auto: 'blue',
      manual: 'gray',
      verified: 'green',
    };
    return variants[confidence] || 'gray';
  };

  const handleCreate = () => {
    const errors = new Set<string>();
    if (!createForm.brand_name.trim()) errors.add('brand_name');
    if (errors.size > 0) {
      setCreateErrors(errors);
      return;
    }
    setCreateErrors(new Set());
    createBrandMutation.mutate({
      brand_name: createForm.brand_name.trim(),
      brand_code: createForm.brand_code.trim() || undefined,
      description: createForm.description.trim() || undefined,
      tagline: createForm.tagline.trim() || undefined,
      brand_main_color: createForm.brand_main_color.trim() || undefined,
      brand_sub_color: createForm.brand_sub_color.trim() || undefined,
      brand_accent_color: createForm.brand_accent_color.trim() || undefined,
      brand_font_families: createForm.brand_font_families || undefined,
    });
  };

  if (isCreating) {
    return (
      <div className="page admin-page brand-detail-page">
        <div className="header-actions">
          <BackButton />
        </div>
        <div className="page-header page-header--child">
          <div className="header-left">
            <h1>ブランド新規作成</h1>
          </div>
        </div>

        <div className="card basic-info-card">
          <h2 className="card-title">
            <img src="/office_stamp_document.svg" alt="" aria-hidden="true" className="card-title-icon" />
            ブランド基本情報
          </h2>
          <table className="info-table">
            <tbody>
              <tr>
                <th>ブランド名 <span className="required-mark">*</span></th>
                <td>
                  <input
                    type="text"
                    className={`inline-edit-input${createErrors.has('brand_name') ? ' error' : ''}`}
                    value={createForm.brand_name}
                    onChange={(e) => setCreateForm({ ...createForm, brand_name: e.target.value })}
                    placeholder="ブランド名を入力"
                    autoFocus
                  />
                  {createErrors.has('brand_name') && (
                    <span className="create-form-error">ブランド名は必須です</span>
                  )}
                </td>
              </tr>
              <tr>
                <th>ブランドコード</th>
                <td>
                  <input
                    type="text"
                    className="inline-edit-input"
                    value={createForm.brand_code}
                    onChange={(e) => setCreateForm({ ...createForm, brand_code: e.target.value })}
                    placeholder="例: showhin"
                  />
                  <span className="inline-edit-hint">商品データを一括アップロードする際にも利用されます。</span>
                </td>
              </tr>
              <tr>
                <th>タグライン</th>
                <td>
                  <input
                    type="text"
                    className="inline-edit-input"
                    value={createForm.tagline}
                    onChange={(e) => setCreateForm({ ...createForm, tagline: e.target.value })}
                    placeholder="ブランドのキャッチフレーズ"
                  />
                </td>
              </tr>
              <tr>
                <th>ブランドカラー</th>
                <td>
                  <div className="brand-colors-group">
                    {([
                      { key: 'brand_main_color', label: 'メイン' },
                      { key: 'brand_sub_color', label: 'サブ' },
                      { key: 'brand_accent_color', label: 'アクセント' },
                    ] as const).map(({ key, label }) => (
                      <div key={key} className="brand-color-row">
                        <span className="brand-color-label">{label}</span>
                        <input
                          type="color"
                          className="brand-color-picker"
                          value={createForm[key] || '#000000'}
                          onChange={(e) => setCreateForm({ ...createForm, [key]: e.target.value })}
                        />
                        <input
                          type="text"
                          className="inline-edit-input brand-color-input"
                          value={createForm[key]}
                          onChange={(e) => setCreateForm({ ...createForm, [key]: e.target.value })}
                          placeholder="#FF5733"
                        />
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
              <tr>
                <th>説明</th>
                <td>
                  <textarea
                    className="inline-edit-textarea"
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    placeholder="ブランドの説明"
                    rows={3}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <BrandFontField
            value={createForm.brand_font_families}
            onChange={(value) => setCreateForm({ ...createForm, brand_font_families: value })}
          />

          <div className="create-form-actions">
            <button
              className="btn-modal-danger"
              onClick={() => navigate('/brands')}
            >
              キャンセル
            </button>
            <button
              className="btn-modal"
              onClick={handleCreate}
              disabled={createBrandMutation.isPending}
            >
              {createBrandMutation.isPending ? '作成中...' : '作成'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page admin-page">
        <div className="admin-loading">
          読み込み中...
        </div>
      </div>
    );
  }

  if (error || !brand) {
    return (
      <div className="page admin-page">
        <div className="header-actions">
          <BackButton />
        </div>
        <div className="admin-alert admin-alert-error">
          ブランドの読み込みに失敗しました: {error instanceof Error ? error.message : '不明なエラー'}
        </div>
      </div>
    );
  }

  const renderMappings = () => (
    <>
          {brand.mappings && brand.mappings.length > 0 ? (
            // 列が多いので、狭いときは表の箱の中で横スクロールさせる (ページをはみ出させない)
            <div className="brand-table-scroll">
            <table className="info-table">
              <thead>
                <tr>
                  <th>プラットフォーム</th>
                  <th>外部ブランド名</th>
                  <th>外部ブランドID</th>
                  <th>登録ID</th>
                  <th>連携方法</th>
                  <th>確認日時</th>
                </tr>
              </thead>
              <tbody>
                {brand.mappings.map((mapping: ECBrandMapping) => (
                  <tr key={mapping.id}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        {mapping.platform === 'amazon' && (
                          <img
                            src="/amazon_icon.svg"
                            alt=""
                            aria-hidden="true"
                            style={{ width: '16px', height: '16px' }}
                          />
                        )}
                        {getPlatformLabel(mapping.platform)}
                        {mapping.marketplace_id && (
                          <span className="marketplace-id"> ({mapping.marketplace_id})</span>
                        )}
                      </span>
                    </td>
                    <td>{mapping.external_brand_name}</td>
                    <td>{mapping.external_brand_id || '-'}</td>
                    <td>
                      {mapping.brand_registry_id ? (
                        <Label variant="green">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <img
                              src="/check_2_green.svg"
                              alt=""
                              aria-hidden="true"
                              style={{ width: '12px', height: '12px' }}
                            />
                            {mapping.platform === 'amazon' ? 'Brand Registry' : '登録済み'}
                          </span>
                        </Label>
                      ) : (
                        <span style={{ color: 'rgba(245, 241, 230, 0.4)' }}>未登録</span>
                      )}
                    </td>
                    <td>
                      <Label variant={getConfidenceVariant(mapping.mapping_confidence)}>
                        {getConfidenceLabel(mapping.mapping_confidence)}
                      </Label>
                    </td>
                    <td>{mapping.verified_at ? formatDate(mapping.verified_at) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : (
            <p className="brand-empty-note">ECサイトとの連携がありません。商品を取得すると自動的に作成されます。</p>
          )}
    </>
  );

  // 見出し帯と概要ボードの表示用
  const primaryLogo = pickPrimaryLogo<BrandLogo>((brand as Brand).logos);
  const logoCount = brand.logos?.length ?? 0;
  const designLanguage = normalizeDesignLanguage(brand.design_language);
  const toneFonts = designLanguage
    ? ((designLanguage.typography.brandFonts ?? []).length > 0
      ? (designLanguage.typography.brandFonts ?? []).slice(0, 2).join(' / ')
      : [designLanguage.typography.display?.family, designLanguage.typography.body?.family].filter(Boolean).join(' / '))
    : '';

  return (
    <div className="page admin-page brand-detail-page">
      {confirmDialog}
      {blocker.state === 'blocked' && (
        <ConfirmDialog
          isOpen
          variant="warning"
          title="未保存の変更があります"
          message="保存していない変更は失われます。このまま移動しますか？"
          confirmLabel="破棄して移動"
          cancelLabel="編集を続ける"
          onConfirm={() => blocker.proceed?.()}
          onCancel={() => blocker.reset?.()}
        />
      )}
      <div className="header-actions">
        <BackButton />
      </div>

      {/* 見出し帯 (案B・利用者指示 2026-10-30): 代表ロゴ・ブランド名 / タグライン (ホバー鉛筆)・ステータス・AI の再生成 */}
      <header className="brand-hero">
        <button
          type="button"
          className={`brand-hero-logo${primaryLogo?.logos?.url ? '' : ' is-empty'}`}
          style={primaryLogo?.logos?.url ? { background: logoCardBackground(primaryLogo) } : undefined}
          onClick={() => (primaryLogo ? selectTab('assets') : setIsLogoModalOpen(true))}
          aria-label={primaryLogo ? 'ロゴを見る' : 'ロゴを追加'}
        >
          {primaryLogo?.logos?.url
            ? <img src={primaryLogo.logos.url} alt="" />
            : <Plus size={20} aria-hidden="true" />}
        </button>
        <div className="brand-hero-meta">
              {/* ブランド名は見出しで直接編集する (基本情報の表から移動・ホバーで鉛筆・利用者指示 2026-10-30) */}
              {editingField === 'brand_name' ? (
                <div className="inline-edit-container brand-hero-name-edit">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (isComposingEnter(e)) return;
                      if (e.key === 'Enter' && editValue.trim()) { e.preventDefault(); saveField(); }
                      if (e.key === 'Escape') { e.preventDefault(); cancelEditing(); }
                    }}
                    className="inline-edit-input brand-hero-name-input"
                    aria-label="ブランド名"
                    autoFocus
                  />
                  <button onClick={saveField} className="inline-save-button" disabled={updateBrandMutation.isPending || !editValue.trim()} title="保存" aria-label="ブランド名を保存">
                    <Check size={18} className="inline-action-icon" />
                  </button>
                  <button onClick={cancelEditing} className="inline-cancel-button" title="キャンセル" aria-label="キャンセル">
                    <X size={18} className="inline-action-icon" />
                  </button>
                </div>
              ) : (
                <div className="inline-edit-hoverable brand-hero-name-row">
                  <h1 className="brand-hero-name">{brand.brand_name}</h1>
                  <button
                    type="button"
                    onClick={() => startEditing('brand_name', brand.brand_name)}
                    className="inline-edit-button"
                    title="編集"
                    aria-label="ブランド名を編集"
                  >
                    <img src="/pencil_1.svg" alt="" className="inline-edit-icon" />
                  </button>
                </div>
              )}
          <InlineEditText
            className="brand-hero-tagline"
            value={brand.tagline || ''}
            placeholder="ブランドのキャッチフレーズ"
            emptyText="タグライン (未設定)"
            ariaLabel="タグライン"
            disabled={updateBrandMutation.isPending}
            onCommit={(tagline) => { updateBrandMutation.mutate({ tagline }); }}
          />
        </div>
        <div className="brand-hero-actions">
          <ToggleSwitch
            checked={brand.is_active}
            onChange={(checked) => updateBrandMutation.mutate({ is_active: checked })}
            label="アクティブ"
          />
          {/* 名前・タグライン・説明・ロゴの候補も作るので見出し帯に置く。結果はトーン＆マナーのタブに出す */}
          <GenerateButton
            className="btn-ai-director-md btn-ai-director-mono"
            label={regenerating ? '生成しています…' : 'AIでブランド情報を再生成'}
            creditCost={regenerateCreditBadge}
            onClick={() => { selectTab('tone'); setRegenerateRequest((n) => n + 1); }}
            disabled={regenerating}
            data-testid="brand-regenerate"
          />
        </div>
      </header>

      {/* タブ (概要 / トーン＆マナー / アセット / ガイドライン / 連携と関連・タブ名は 2026-10-30 に「ブランド資産」「守るルール」から改名)。見た目は案件ページのタブと共通 */}
      <nav className="products-page-tabs-bar brand-tabs-bar" aria-label="ブランドの項目">
        <div className="products-page-tabs brand-tabs" role="tablist">
          {BRAND_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`brand-tab-${t.key}`}
              aria-selected={activeTab === t.key}
              aria-controls={`brand-panel-${t.key}`}
              className={`products-tab brand-tab${activeTab === t.key ? ' active' : ''}`}
              onClick={() => selectTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Error messages */}
      {updateBrandMutation.isError && (
        <div className="error-message">
          更新に失敗しました: {updateBrandMutation.error instanceof Error ? updateBrandMutation.error.message : '不明なエラー'}
        </div>
      )}
      {deleteBrandMutation.isError && (
        <div className="error-message">
          削除に失敗しました: {deleteBrandMutation.error instanceof Error ? deleteBrandMutation.error.message : '不明なエラー'}
        </div>
      )}


      {/* パネルは全部マウントしたまま隠す (トーン＆マナーの下書きや再生成の進行をタブ切り替えで失わないため)。
          概要以外のタブは 2 つの型のどちらか (利用者指示 2026-10-30): brand-panel--full = 全幅で縦に積む (トーン＆マナー)、
          brand-panel--split = 幅があれば 2 段組み (アセット・ガイドライン・連携と関連) */}
      <section id="brand-panel-overview" role="tabpanel" aria-labelledby="brand-tab-overview" hidden={activeTab !== 'overview'} className="brand-panel">
        <div className="brand-board">
          <section className="brand-board-card brand-board-card--wide" aria-labelledby="board-basic">
            <div className="brand-board-head"><h2 id="board-basic">基本情報</h2></div>
            <InlineEditText
              multiline
              className="brand-board-description"
              value={brand.description || ''}
              placeholder="ブランドの説明"
              emptyText="説明 (未設定)"
              ariaLabel="説明"
              disabled={updateBrandMutation.isPending}
              onCommit={(description) => { updateBrandMutation.mutate({ description }); }}
            />
            <dl className="brand-board-facts">
              <div>
                <dt>ブランドコード
                  <HelpTooltip ariaLabel="ブランドコードのヘルプ">
                    組織内でブランドを識別する内部コードです。商品データを CSV / JSON で一括アップロードするとき、brand_code 列にこの値を入れるとこのブランドに紐づきます。
                  </HelpTooltip>
                </dt>
                <dd>
                  <InlineEditText
                    value={brand.brand_code || ''}
                    placeholder="例: showhin"
                    ariaLabel="ブランドコード"
                    disabled={updateBrandMutation.isPending}
                    onCommit={(brand_code) => { updateBrandMutation.mutate({ brand_code }); }}
                  />
                </dd>
              </div>
              <div>
                <dt>ブランドURL</dt>
                <dd>
                  {brandUrls.length > 0
                    ? <a href={brandUrls[0].url} target="_blank" rel="noreferrer" className="brand-board-link">{brandUrls[0].name || brandUrls[0].url}</a>
                    : null}
                  {brandUrls.length > 1 && <span className="brand-board-muted"> ほか{brandUrls.length - 1}件</span>}
                  {/* 追加・編集は「連携と関連」タブのブランドURL 欄で行う (ここから移動する) */}
                  <button type="button" className="brand-board-more brand-board-inline-action" onClick={() => selectTab('links')}>
                    {brandUrls.length > 0 ? '編集 ›' : '＋ URLを追加'}
                  </button>
                </dd>
              </div>
              <div>
                <dt>最終更新</dt>
                <dd className="brand-board-muted">{formatDate(brand.updated_at)}</dd>
              </div>
            </dl>
          </section>

          <section className="brand-board-card" aria-labelledby="board-related">
            <div className="brand-board-head">
              <h2 id="board-related">関連</h2>
              <button type="button" className="brand-board-more" onClick={() => selectTab('links')}>すべて見る ›</button>
            </div>
            <div className="brand-board-stats">
              <Link to={`/products/manual?brand_id=${id}`} className="brand-board-stat">
                <span>{brandConfig.labels.productShort}</span>
                <strong>{(stats?.total_products ?? 0).toLocaleString()}</strong>
              </Link>
              <Link to={`/materials/shared?brand_id=${id}`} className="brand-board-stat">
                <span>素材</span>
                <strong>{((stats?.cloud_materials_count ?? 0) + (stats?.temporary_materials_count ?? 0)).toLocaleString()}</strong>
              </Link>
            </div>
            {brandConfig.features.amazonIntegration && (
              <p className="brand-board-muted">
                {(stats?.active_mappings ?? 0) > 0 ? `ECサイトのブランドと${stats?.active_mappings}件連携中` : 'ECサイト連携なし'}
              </p>
            )}
          </section>

          <section className="brand-board-card brand-board-card--wide" aria-labelledby="board-tone">
            <div className="brand-board-head">
              <h2 id="board-tone">トーン＆マナー</h2>
              <button type="button" className="brand-board-more" onClick={() => selectTab('tone')}>すべて見る ›</button>
            </div>
            {designLanguage && designLanguage.palette.length > 0 ? (
              <div className="brand-board-palette" aria-label="ブランドカラー">
                {designLanguage.palette.slice(0, 6).map((c, i) => (
                  <span key={`${c.hex}-${i}`} style={{ background: c.hex, flexGrow: i === 0 ? 3 : 2 }} title={`${c.hex} (${roleLabel(c.role)})`} />
                ))}
              </div>
            ) : (
              <p className="brand-board-muted">ブランドカラーが登録されていません。</p>
            )}
            <div className="brand-board-tone-facts">
              {(designLanguage?.palette ?? []).filter((c) => c.role === 'primary' || c.role === 'secondary').slice(0, 2).map((c, i) => (
                <span key={`${c.hex}-${i}`}><span className="brand-board-muted">{roleLabel(c.role)} </span><span className="brand-board-mono">{c.hex}</span></span>
              ))}
              {toneFonts && <span><span className="brand-board-muted">書体 </span>{toneFonts}</span>}
              {designLanguage && designLanguage.mood.length > 0 && <span><span className="brand-board-muted">ムード </span>{designLanguage.mood.slice(0, 4).join('・')}</span>}
            </div>
          </section>

          <section className="brand-board-card" aria-labelledby="board-assets">
            <div className="brand-board-head">
              <h2 id="board-assets">アセット</h2>
              <button type="button" className="brand-board-more" onClick={() => selectTab('assets')}>すべて見る ›</button>
            </div>
            {/* 各項目の先頭はアセットのタブの見出しと同じアイコン (利用者指示 2026-10-30) */}
            <ul className="brand-board-list">
              <li><span className="brand-board-list-label"><img src="/copyright_and_protecttion_1.svg" alt="" aria-hidden="true" />ロゴ</span><span className={logoCount ? '' : 'brand-board-muted'}>{logoCount ? `${logoCount}件` : '未登録'}</span></li>
              <li><span className="brand-board-list-label"><img src="/vintage_tv_4.svg" alt="" aria-hidden="true" />ブランドカット</span><span className={brandCutCount ? '' : 'brand-board-muted'}>{brandCutCount ? `${brandCutCount}件` : '未登録'}</span></li>
              <li><span className="brand-board-list-label"><img src="/music_on_off_1.svg" alt="" aria-hidden="true" />サウンドロゴ</span><span className={soundLogoCount ? '' : 'brand-board-muted'}>{soundLogoCount ? `${soundLogoCount}件` : '未登録'}</span></li>
            </ul>
          </section>

          <section className="brand-board-card brand-board-card--full" aria-labelledby="board-rules">
            <div className="brand-board-head">
              <h2 id="board-rules">ガイドライン</h2>
              <button type="button" className="brand-board-more" onClick={() => selectTab('rules')}>すべて見る ›</button>
            </div>
            {brand.brand_regulation || guidelines.length > 0 ? (
              <div className="brand-board-rules">
                {brand.brand_regulation && <p className="brand-board-clamp">{brand.brand_regulation}</p>}
                {guidelines.length > 0 && <p className="brand-board-muted">ガイドライン画像 {guidelines.length}枚</p>}
              </div>
            ) : (
              <p className="brand-board-muted">レギュレーション・ガイドライン画像は未設定です。設定すると、生成のときに必ず守る決まりとして使われます。</p>
            )}
          </section>
        </div>
      </section>

      <section id="brand-panel-tone" role="tabpanel" aria-labelledby="brand-tab-tone" hidden={activeTab !== 'tone'} className="brand-panel brand-panel--full">
        {id && (
          <BrandToneMannerSection
            brandId={id}
            designLanguage={brand.design_language}
            identity={{ brand_name: brand.brand_name, tagline: brand.tagline, description: brand.description }}
            onSave={(designLanguage) => updateBrandMutation.mutateAsync({ design_language: designLanguage })}
            onApplyIdentity={(patch) => updateBrandMutation.mutateAsync(patch)}
            onLogoRegistered={() => { queryClient.invalidateQueries({ queryKey: ['brand', id] }); }}
            onDirtyChange={setToneMannerDirty}
            regenerateRequest={regenerateRequest}
            onRegenerateRunningChange={setRegenerating}
          />
        )}
      </section>

      <section id="brand-panel-assets" role="tabpanel" aria-labelledby="brand-tab-assets" hidden={activeTab !== 'assets'} className="brand-panel brand-panel--split">
        <div className="card logos-card">
          <div className="card-header-with-action">
            <h2 className="card-title">
              <img src="/copyright_and_protecttion_1.svg" alt="" aria-hidden="true" className="card-title-icon" />
              ロゴ
            </h2>
            {logoSheetError && (
              <span className="logo-sheet-status">
                ロゴシートの再生成に失敗しました
                <button
                  type="button"
                  className="logo-sheet-retry"
                  onClick={() => runLogoSheetRegen(true)}
                >
                  再生成
                </button>
              </span>
            )}
          </div>

          {brand.logos && brand.logos.length > 0 ? (
            <div className="logos-grid">
              {[...brand.logos].sort((a, b) => Number(b.is_default ?? false) - Number(a.is_default ?? false)).map((brandLogo: BrandLogo) => (
                <div
                  key={brandLogo.id}
                  className={`logo-item ${draggedLogoId === brandLogo.id ? 'dragging' : ''} ${dragOverLogoId === brandLogo.id ? 'drag-over' : ''}`}
                  draggable
                  onDragStart={(e) => handleLogoDragStart(e, brandLogo.id)}
                  onDragEnd={handleLogoDragEnd}
                  onDragEnter={(e) => handleLogoDragEnter(e, brandLogo.id)}
                  onDragLeave={handleLogoDragLeave}
                  onDragOver={handleLogoDragOver}
                  onDrop={(e) => handleLogoDrop(e, brandLogo.id)}
                >
                  <button
                    type="button"
                    className="logo-edit-button"
                    onClick={() => setEditingLogo(brandLogo)}
                    title="編集"
                  >
                    <img src="/pencil_1.svg" alt="編集" />
                  </button>
                  {brandLogo.logos?.url && (
                    <div
                      className="logo-image-container"
                      style={{
                        background: brandLogo.background === 'transparent' || brandLogo.color_type === 'white'
                          ? 'repeating-conic-gradient(rgba(255, 255, 255, 0.1) 0% 25%, rgba(40, 40, 40, 1) 0% 50%) 50% / 12px 12px'
                          : undefined,
                      }}
                    >
                      {brandLogo.is_default && (
                        <span className="default-badge">デフォルト</span>
                      )}
                      <img
                        src={brandLogo.logos.url}
                        alt={getLogoAttributeText(brandLogo).join(' / ') || '属性なし'}
                        className="logo-image"
                      />
                    </div>
                  )}
                  <div className="logo-info">
                    <div className="logo-variant-type">
                      {getLogoAttributeText(brandLogo).length > 0
                        ? getLogoAttributeText(brandLogo).map((attr, i) => (
                            <span key={i} className="logo-attr-line">{attr}</span>
                          ))
                        : '属性なし'}
                    </div>
                    {brandLogo.description && (
                      <div className="logo-description">{brandLogo.description}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="brand-empty-note">ロゴが登録されていません。</p>
          )}
          {/* 追加はグリッドの下の共通の「〜を追加」ボタン (ブランドカット・サウンドロゴ等と同じ見た目・利用者指示 2026-10-30) */}
          <div className="brand-add-row">
            <button type="button" className="brand-add-button" onClick={() => setIsLogoModalOpen(true)}>
              <Plus size={14} aria-hidden="true" />
              ロゴを追加
            </button>
          </div>

        </div>

        <BrandCutSection
          brandId={id || ''}
          brandName={brand.brand_name}
          tagline={brand.tagline}
          description={brand.description}
          brandMainColor={brand.brand_main_color}
          brandSubColor={brand.brand_sub_color}
          brandAccentColor={brand.brand_accent_color}
          logos={(brand.logos ?? []).map((l: BrandLogo) => ({
            id: l.id,
            description: l.description,
            background: l.background,
            color_type: l.color_type,
            layout: l.layout,
            is_default: l.is_default,
            logos: l.logos ? { url: l.logos.url, file_name: l.logos.file_name } : undefined,
          }))}
        />

        <SoundLogoSection brandId={id || ''} brandName={brand.brand_name} tagline={brand.tagline} />
      </section>

      <section id="brand-panel-rules" role="tabpanel" aria-labelledby="brand-tab-rules" hidden={activeTab !== 'rules'} className="brand-panel">
        {/* タブ名が「ガイドライン」なので欄全体の見出しは出さず、画像とレギュレーションを 2 段組みに並べる (利用者指示 2026-10-30) */}
        <div className="card guidelines-card">
          <div className="guideline-subsections">
          <div className="guideline-subsection">
          <div className="card-header-with-action">
            <h2 className="card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <img src="/lock_shield.svg" alt="" aria-hidden="true" className="card-title-icon" style={{ marginRight: 0 }} />
              ブランドガイドライン画像
              <HelpTooltip ariaLabel="ブランドガイドライン画像のヘルプ">
                ブランドガイドラインの中から、ロゴの禁止事項ルールやカラーパレットなどのページのみを画像化してアップロードしてください。AI画像生成時に参考として利用されます。
              </HelpTooltip>
            </h2>
          </div>

          {guidelines.length > 0 ? (
            <div className="guideline-single">
              {guidelines.map((guideline) => (
                <div key={guideline.id} className="guideline-item">
                  <button
                    type="button"
                    className="guideline-delete-button"
                    onClick={() => handleGuidelineDelete(guideline.id)}
                    title="削除"
                  >
                    ×
                  </button>
                  {guideline.url && (
                    <div className="guideline-image-container">
                      <img
                        src={guideline.url}
                        alt={guideline.file_name}
                        className="guideline-image"
                      />
                    </div>
                  )}
                  <div className="guideline-info">
                    <div className="guideline-filename">{guideline.file_name}</div>
                    {guideline.description && (
                      <div className="guideline-description">{guideline.description}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className={`guideline-inline-dropzone ${guidelineDragOver ? 'drag-over' : ''} ${isGuidelineUploading ? 'uploading' : ''}`}
              onClick={() => !isGuidelineUploading && guidelineFileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setGuidelineDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setGuidelineDragOver(false);
              }}
              onDrop={handleGuidelineDrop}
            >
              <div className="guideline-modal-drop-text">
                {isGuidelineUploading ? 'アップロード中...' : 'クリックまたはドラッグ&ドロップで画像を追加'}
              </div>
              <div className="guideline-modal-drop-hint">PNG, JPG, WebP形式（PDFは不可）</div>
            </div>
          )}

          </div>
          <div className="guideline-subsection">
          <div className="brand-regulation-section">
            <div className="card-header-with-action">
              <h2 className="card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <img src="/office_stamp_document.svg" alt="" aria-hidden="true" className="card-title-icon" style={{ marginRight: 0 }} />
                ブランドレギュレーション
                <HelpTooltip ariaLabel="ブランドレギュレーションのヘルプ">
                  禁止事項などを記載すると、AI画像・動画生成に使用されます。
                </HelpTooltip>
              </h2>
            </div>
            {/* 平時は文字 + 鉛筆、鉛筆で編集 (ページでは入力欄を既定で出さない・利用者指示 2026-10-30) */}
            <InlineEditText
              multiline
              className="brand-regulation-editor"
              value={brand.brand_regulation || ''}
              placeholder="他社のキャラクターを出さない、肌の過度な露出を避ける、など"
              emptyText="未設定 (例: 他社のキャラクターを出さない、肌の過度な露出を避ける)"
              ariaLabel="ブランドレギュレーション"
              disabled={updateBrandMutation.isPending}
              onCommit={(value) => { updateBrandMutation.mutate({ brand_regulation: value }); }}
            />
          </div>
          </div>
          </div>

          <input
            ref={guidelineFileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleGuidelineFileChange}
            style={{ display: 'none' }}
          />
        </div>
      </section>

      <section id="brand-panel-links" role="tabpanel" aria-labelledby="brand-tab-links" hidden={activeTab !== 'links'} className="brand-panel brand-panel--split">
        {id && <BrandUrlSection brandId={id} />}
        {/* 連携があるときは表の列が多いので 2 段組みの中でも全幅にする */}
        {brandConfig.features.amazonIntegration && (
        <div className={`card mappings-card${brand.mappings && brand.mappings.length > 0 ? ' brand-panel-wide' : ''}`}>
          <h2 className="card-title">
            <img src="/database_1.svg" alt="" aria-hidden="true" className="card-title-icon" />
            ECサイトブランド
          </h2>
          {renderMappings()}
        </div>
        )}
      <div className="section-row">
        {/* 関連商品: プラットフォームと商品数のみ */}
        <div className="card stats-card">
          <h2 className="card-title">
            <img src="/shipment_barcode.svg" alt="" aria-hidden="true" className="card-title-icon" />
            関連{brandConfig.labels.productShort}
          </h2>

          {stats ? (
            stats.products_by_platform && stats.products_by_platform.length > 0 ? (
              <table className="info-table info-table--plain-head">
                <thead>
                  <tr>
                    <th>プラットフォーム</th>
                    <th>{brandConfig.labels.productShort}数</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.products_by_platform.map((platformStat: BrandStats['products_by_platform'][number], index: number) => {
                    const productsHref =
                      platformStat.platform === 'amazon'
                        ? `/products/amazon?brand_id=${id}`
                        : platformStat.platform === 'manual'
                          ? `/products/manual?brand_id=${id}`
                          : null;
                    return (
                      <tr key={index}>
                        <th>
                          {getPlatformLabel(platformStat.platform)}
                          {platformStat.marketplace_id && ` (${platformStat.marketplace_id})`}
                        </th>
                        <td>
                          <CountPill count={platformStat.count} href={productsHref} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <table className="info-table info-table--plain-head">
                <thead>
                  <tr>
                    <th>プラットフォーム</th>
                    <th>{brandConfig.labels.productShort}数</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>-</th>
                    <td>
                      <CountPill count={0} />
                    </td>
                  </tr>
                </tbody>
              </table>
            )
          ) : (
            <div className="loading-stats">統計情報を読み込み中...</div>
          )}
        </div>

        {/* 素材: 共有フォルダ / 個人フォルダ */}
        <div className="card stats-card">
          <h2 className="card-title">
            <img src="/picture_stack_landscape_3.svg" alt="" aria-hidden="true" className="card-title-icon" />
            素材
          </h2>

          {stats ? (
            <table className="info-table">
              <thead>
                <tr>
                  <th>
                    <img src="/cloud_file.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '6px', verticalAlign: 'middle' }} />
                    共有フォルダ
                  </th>
                  <th>
                    <img src="/single_neutral_folder_box.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '6px', verticalAlign: 'middle' }} />
                    個人フォルダ
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <CountPill
                      count={stats.cloud_materials_count}
                      href={`/materials/shared?brand_id=${id}`}
                    />
                  </td>
                  <td>
                    <CountPill
                      count={stats.temporary_materials_count}
                      href={`/materials/personal?brand_id=${id}`}
                      variant="personal"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="loading-stats">統計情報を読み込み中...</div>
          )}
        </div>
      </div>
      </section>


      {/* Logo Management Modal */}
      <LogoManagementModal
        isOpen={isLogoModalOpen}
        onClose={() => setIsLogoModalOpen(false)}
        brandId={id || ''}
      />

      {/* Logo Edit Modal */}
      <LogoEditModal
        isOpen={!!editingLogo}
        onClose={() => setEditingLogo(null)}
        brandId={id || ''}
        brandLogo={editingLogo}
      />

      {/* ブランドの削除は「連携と関連」タブの最下部だけに出す (どのタブにも赤いボタンが出ると目障りなため) */}
      {activeTab === 'links' && (
      <div className="brand-delete-footer">
        <button onClick={handleDelete} className="btn-modal-danger brand-delete-button">
          <img src="/bin_1.svg" alt="" aria-hidden="true" className="brand-delete-icon" />
          ブランドを削除
        </button>
      </div>
      )}
    </div>
  );
}
